/**
 * Master/detail mega menu.
 *
 * Left column lists the categories; clicking one reveals its sub-categories in
 * the right column instead of navigating. Categories without sub-categories
 * keep their default link behaviour. The first category is active on load and
 * the markup is fully functional without JS (graceful degradation).
 *
 * @extends {HTMLElement}
 */
class MegaMenuCategories extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /**
   * Switches the active category/panel when a category with sub-categories is clicked.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const target = /** @type {Element} */ (event.target);
    const trigger = target.closest('[data-category-index]');
    if (!(trigger instanceof HTMLElement)) return;

    const { categoryIndex } = trigger.dataset;
    if (categoryIndex == null) return;

    // No panel for this category (no sub-categories) → let the link navigate.
    const panel = this.querySelector(`[data-panel-index="${categoryIndex}"]`);
    if (!panel) return;

    event.preventDefault();
    this.#activate(categoryIndex);
  };

  /**
   * Marks the given category and its panel as active, hiding the others.
   * @param {string} index
   */
  #activate(index) {
    for (const category of this.querySelectorAll('[data-category-index]')) {
      category.classList.toggle('is-active', category instanceof HTMLElement && category.dataset.categoryIndex === index);
    }

    for (const panel of this.querySelectorAll('[data-panel-index]')) {
      if (!(panel instanceof HTMLElement)) continue;
      const isActive = panel.dataset.panelIndex === index;
      panel.classList.toggle('is-active', isActive);
      panel.hidden = !isActive;
    }
  }
}

if (!customElements.get('mega-menu-categories')) {
  customElements.define('mega-menu-categories', MegaMenuCategories);
}
